import type {
  AppNotification,
  Channel,
  ChannelMessage,
  Conversation,
  DirectMessage,
  MessageReaction,
  UnreadCounts,
  UserSummary,
  VoiceParticipant,
  CallState,
  StreamSettings,
} from "./types";
import type { Activity, CustomStatus, Modal, PresenceState } from "./rich";
import type { MessagePoll } from "./extras";

export interface PresenceEvent {
  userId: string;
  /** Kept for older clients: anything but "offline". */
  online: boolean;
  status: PresenceState;
  customStatus: CustomStatus | null;
  activity: Activity | null;
  lastSeenAt: string | null;
}

/** Events the server pushes to clients over Socket.IO. */
export interface ServerToClientEvents {
  /** Sent once the socket has joined all its rooms; from here on no channel event is missed. */
  "ready": () => void;
  "message:new": (m: ChannelMessage) => void;
  "message:update": (m: ChannelMessage) => void;
  "message:delete": (p: { id: string; channelId: string }) => void;
  "message:reactions": (p: { id: string; channelId: string; reactions: Omit<MessageReaction, "me">[]; userId: string; emoji: string; added: boolean }) => void;
  /**
   * A chat poll's counts changed (vote or early end). `poll.myVotes` is always empty:
   * clients keep their own, except when `voterId` is them, in which case `voterVotes` replaces it.
   */
  "message:poll": (p: { id: string; channelId: string; poll: MessagePoll; voterId: string | null; voterVotes: string[] }) => void;
  "channel:typing": (p: { channelId: string; userId: string; displayName: string }) => void;
  /** Community structure changed (channels, roles, settings, membership). Clients refetch. */
  "community:update": (p: { communityId: string }) => void;
  "community:removed": (p: { communityId: string }) => void;
  "channel:created": (c: Channel) => void;
  "presence": (p: PresenceEvent) => void;
  /** Only sent to the user who triggered it (ephemeral command responses, automod notices). */
  "message:ephemeral": (m: ChannelMessage) => void;
  "message:bulk-delete": (p: { ids: string[]; channelId: string }) => void;
  "thread:update": (p: { channelId: string; threadId: string }) => void;
  "thread:delete": (p: { channelId: string; threadId: string }) => void;
  /** Open a modal form requested by an interaction response. */
  "interaction:modal": (p: { interactionId: string; modal: Modal }) => void;
  /** An interaction was acknowledged; the client stops its "thinking" state. */
  "interaction:result": (p: { interactionId: string; ok: boolean; error?: string; deferred?: boolean }) => void;
  "announcement": (p: { id: string; title: string; body: string; level: string }) => void;
  "relationships": () => void;
  /** The account changed outside this client (a staff action or a role change). Clients refetch the current user. */
  "me:update": () => void;
  "event:update": (p: { communityId: string; eventId: string }) => void;
  "post:counts": (p: { id: string; likeCount: number; commentCount: number; repostCount: number }) => void;

  "dm:new": (m: DirectMessage) => void;
  "dm:update": (m: DirectMessage) => void;
  "dm:delete": (p: { id: string; conversationId: string }) => void;
  "dm:typing": (p: { conversationId: string; userId: string; displayName: string }) => void;
  "dm:read": (p: { conversationId: string; userId: string; lastReadMessageId: string }) => void;
  "dm:delivered": (p: { conversationId: string; userId: string; messageId: string }) => void;
  "dm:reactions": (p: { id: string; conversationId: string; reactions: Omit<MessageReaction, "me">[]; userId: string; emoji: string; added: boolean }) => void;
  "conversation:update": (c: Conversation) => void;

  "notification:new": (n: AppNotification) => void;
  "unread": (u: UnreadCounts) => void;

  /** Who is in a voice or stage channel. Stage channels also carry the live stage's topic. */
  "voice:state": (p: { communityId: string; channelId: string; participants: VoiceParticipant[]; topic?: string }) => void;
  "voice:peer-joined": (p: { channelId: string; socketId: string; userId: string }) => void;
  "voice:peer-left": (p: { channelId: string; socketId: string }) => void;
  "voice:signal": (p: { from: string; data: VoiceSignal }) => void;
  "voice:kicked": (p: { channelId: string; reason: string }) => void;

  /** A conversation's call started, changed or ended (null). Sent to every member. */
  "call:state": (p: { conversationId: string; call: CallState | null }) => void;
  /** You're being rung. */
  "call:ring": (p: { conversationId: string; from: UserSummary; call: CallState }) => void;
  /** Stop ringing (answered, declined on another device, timed out or the call ended). */
  "call:ring-end": (p: { conversationId: string }) => void;
}

/** Limits of the peer-to-peer voice mesh. */
export const VOICE_LIMITS = {
  /** Everyone connects to everyone in voice channels and calls. */
  mesh: 12,
  /** Stage speakers connect to everyone; the audience only to speakers. */
  stageSpeakers: 10,
  stageParticipants: 50,
  /** How long a call rings before it gives up. */
  ringSeconds: 35,
  stageTopic: 120,
} as const;

export const STREAM_RESOLUTIONS = ["720", "1080", "source"] as const;
export const STREAM_FRAME_RATES = [15, 30, 60] as const;
/** Largest stream preview image (a JPEG data URL) the server accepts. */
export const STREAM_PREVIEW_MAX = 40_000;

export interface VoiceJoinResult {
  peers: { socketId: string; userId: string }[];
  kind: "voice" | "stage" | "call";
  /** Stage channels: whether you joined as a speaker. */
  speaker?: boolean;
  topic?: string;
}

export type VoiceSignal =
  | { type: "offer"; sdp: string }
  | { type: "answer"; sdp: string }
  | { type: "candidate"; candidate: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null } }
  /** Ask a streaming peer to start (or stop) sending you their screen. */
  | { type: "watch"; on: boolean };

type Ack<T = unknown> = (res: { ok: true; data?: T } | { ok: false; error: string }) => void;

/** Events clients send to the server. */
export interface ClientToServerEvents {
  "channel:typing": (p: { channelId: string }) => void;
  "dm:typing": (p: { conversationId: string }) => void;
  /** Explicit status choice for this account (applies to every device). */
  "presence:status": (p: { status: "online" | "idle" | "dnd" | "invisible" }) => void;
  /** This device became idle / active (no input for a while, or tab hidden). */
  "presence:idle": (p: { idle: boolean }) => void;
  /** What the user is looking at, for "active in" presence. */
  "presence:focus": (p: { communityId: string | null }) => void;
  /** Confirms a DM arrived on this device (delivery receipts). */
  "dm:delivered": (p: { conversationId: string; messageId: string }) => void;
  /** Round trip to check the connection is alive (after sleep, a hidden tab or a network change). */
  "sync:probe": (ack: () => void) => void;
  /** Join a voice or stage channel (leaving any other voice room or call). */
  "voice:join": (p: { channelId: string }, ack: Ack<VoiceJoinResult>) => void;
  "voice:leave": () => void;
  "voice:state": (p: { muted: boolean; deafened: boolean }) => void;
  "voice:signal": (p: { to: string; data: VoiceSignal }) => void;
  /**
   * Start or stop sharing your screen in your current voice room. Needs Share screen (and to be a speaker on stage).
   * Sending `settings` while already live updates them (quality change, new source with or without audio).
   */
  "voice:stream": (p: { streaming: boolean; settings?: StreamSettings }, ack: Ack) => void;
  /** While streaming: a small snapshot of the stream for people who aren't watching. A JPEG data URL. */
  "voice:preview": (p: { image: string }) => void;
  /** The latest snapshot of someone's stream (anyone who can see the room). */
  "voice:preview:get": (p: { socketId: string }, ack: Ack<{ image: string | null; at: string | null }>) => void;
  /** Stage audience: raise or lower your hand. */
  "stage:hand": (p: { raised: boolean }) => void;
  /** Stage moderators: invite to speak / move to the audience. Anyone can move themselves to the audience. */
  "stage:speaker": (p: { channelId: string; userId: string; speaker: boolean }, ack: Ack) => void;
  "stage:topic": (p: { channelId: string; topic: string }, ack: Ack) => void;
  "stage:info": (p: { channelId: string }, ack: Ack<{ topic: string }>) => void;
  /** Start a call in a conversation, or join the one in progress (accepting a ring). */
  "call:join": (p: { conversationId: string }, ack: Ack<VoiceJoinResult & { call: CallState }>) => void;
  "call:decline": (p: { conversationId: string }) => void;
  /** Ring a member who isn't in the call (or everyone missing when userId is omitted). */
  "call:ring": (p: { conversationId: string; userId?: string }, ack: Ack) => void;
}
